import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import FunnelLayout from "../FunnelLayout";
import { useFunnelForm } from "../useFunnelForm";
import { updateStep2 } from "../api";

const MAX_PEOPLE = 10;

function clampList(list, length, fill = "") {
  if (list.length === length) return list;
  if (list.length > length) return list.slice(0, length);
  return [...list, ...Array(length - list.length).fill(fill)];
}

function ShieldCheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}

function UserIcon({ size = 22 }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="8" r="4" />
      <path d="M4 22a8 8 0 0116 0" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="24"
      height="22"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2 21a7 7 0 0114 0" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M22 19a5 5 0 00-7-4.6" />
    </svg>
  );
}

function CheckCircleSolidIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="currentColor" />
      <polyline
        points="9 12 11 14 16 9"
        fill="none"
        stroke="#ffffff"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="22"
      height="22"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="12" x2="12" y2="16" />
      <circle cx="12" cy="8" r="0.5" fill="currentColor" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0110 0v4" />
    </svg>
  );
}

const validateAdultAge = (raw) => {
  if (raw === "" || raw == null) return "Required.";
  const n = Number(raw);
  if (!Number.isFinite(n)) return "Enter a number.";
  if (n < 18) return "Must be 18 or older.";
  if (n > 120) return "Please enter a realistic age.";
  return null;
};

const validateChildAge = (raw) => {
  if (raw === "" || raw == null) return "Required.";
  const n = Number(raw);
  if (!Number.isFinite(n)) return "Enter a number.";
  if (n < 0) return "Age cannot be negative.";
  if (n > 17) return "Must be under 18.";
  return null;
};

export default function Step2() {
  const navigate = useNavigate();
  const { data, update } = useFunnelForm();

  const initialAdultCount =
    typeof data.step2?.adultCount === "number" ? data.step2.adultCount : 1;
  const initialChildCount =
    typeof data.step2?.childCount === "number" ? data.step2.childCount : 0;
  const initialAdultAges = clampList(
    (data.step2?.adultAges || []).map((v) => (v == null || v === "" ? "" : String(v))),
    initialAdultCount
  );
  const initialChildAges = clampList(
    (data.step2?.childAges || []).map((v) => (v == null || v === "" ? "" : String(v))),
    initialChildCount
  );

  const [adultCount, setAdultCount] = useState(initialAdultCount);
  const [childCount, setChildCount] = useState(initialChildCount);
  const [adultAges, setAdultAges] = useState(initialAdultAges);
  const [childAges, setChildAges] = useState(initialChildAges);
  const [adultErrors, setAdultErrors] = useState(() =>
    Array(initialAdultCount).fill(null)
  );
  const [childErrors, setChildErrors] = useState(() =>
    Array(initialChildCount).fill(null)
  );
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");

  const changeAdultCount = (delta) => {
    const next = Math.max(1, Math.min(MAX_PEOPLE, adultCount + delta));
    if (next === adultCount) return;
    setAdultCount(next);
    setAdultAges((prev) => clampList(prev, next));
    setAdultErrors((prev) => clampList(prev, next, null));
  };

  const changeChildCount = (delta) => {
    const next = Math.max(0, Math.min(MAX_PEOPLE, childCount + delta));
    if (next === childCount) return;
    setChildCount(next);
    setChildAges((prev) => clampList(prev, next));
    setChildErrors((prev) => clampList(prev, next, null));
  };

  const setAdultAge = (idx, raw) => {
    const cleaned = raw.replace(/\D/g, "").slice(0, 3);
    setAdultAges((prev) => prev.map((a, i) => (i === idx ? cleaned : a)));
    setAdultErrors((prev) => prev.map((e, i) => (i === idx ? null : e)));
  };

  const setChildAge = (idx, raw) => {
    const cleaned = raw.replace(/\D/g, "").slice(0, 3);
    setChildAges((prev) => prev.map((a, i) => (i === idx ? cleaned : a)));
    setChildErrors((prev) => prev.map((e, i) => (i === idx ? null : e)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextAdultErrors = adultAges.map(validateAdultAge);
    const nextChildErrors = childAges.map(validateChildAge);
    const hasErrors =
      nextAdultErrors.some(Boolean) || nextChildErrors.some(Boolean);
    if (hasErrors) {
      setAdultErrors(nextAdultErrors);
      setChildErrors(nextChildErrors);
      return;
    }
    if (submitting) return;

    const step2 = {
      adultCount,
      childCount,
      adultAges: adultAges.map(Number),
      childAges: childAges.map(Number)
    };
    update("step2", step2);

    if (!data.leadId) {
      navigate("/v2/step-1");
      return;
    }

    setApiError("");
    setSubmitting(true);
    try {
      await updateStep2(data.leadId, {
        step1: data.step1,
        step2,
        createdAt: data.createdAt,
        tracking: data.tracking,
        pageUrl: window.location.href
      });
      navigate("/v2/step-3");
    } catch (err) {
      setApiError("We couldn't save your info. Please try again.");
      setSubmitting(false);
    }
  };

  const totalPeople = adultCount + childCount;

  return (
    <FunnelLayout currentStep={2}>
      <div className="fv2-step2">
        <h1 className="fv2-step2-h1">Tell Us About Your Family</h1>
        <p className="fv2-step2-sub">
          Helps us match plan options to your household.
        </p>

        <form onSubmit={handleSubmit} noValidate>
          <h3 className="fv2-step2-section-h">
            How many people are in your household?
          </h3>

          <div className="fv2-step2-counter-row">
            <div className="fv2-step2-counter-card">
              <div className="fv2-step2-counter-text">
                <span className="fv2-step2-counter-icon">
                  <UserIcon size={22} />
                </span>
                <div>
                  <strong>Adults (18+)</strong>
                </div>
              </div>
              <div
                className="fv2-step2-counter-controls"
                role="group"
                aria-label="Adults count"
              >
                <button
                  type="button"
                  className="fv2-step2-counter-btn"
                  onClick={() => changeAdultCount(-1)}
                  disabled={adultCount <= 1}
                  aria-label="Decrease adults"
                >
                  −
                </button>
                <span
                  className="fv2-step2-counter-value"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {adultCount}
                </span>
                <button
                  type="button"
                  className="fv2-step2-counter-btn"
                  onClick={() => changeAdultCount(1)}
                  disabled={adultCount >= MAX_PEOPLE}
                  aria-label="Increase adults"
                >
                  +
                </button>
              </div>
            </div>

            <div className="fv2-step2-counter-card">
              <div className="fv2-step2-counter-text">
                <span className="fv2-step2-counter-icon">
                  <UsersIcon />
                </span>
                <div>
                  <strong>Children (Under 18)</strong>
                </div>
              </div>
              <div
                className="fv2-step2-counter-controls"
                role="group"
                aria-label="Children count"
              >
                <button
                  type="button"
                  className="fv2-step2-counter-btn"
                  onClick={() => changeChildCount(-1)}
                  disabled={childCount <= 0}
                  aria-label="Decrease children"
                >
                  −
                </button>
                <span
                  className="fv2-step2-counter-value"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {childCount}
                </span>
                <button
                  type="button"
                  className="fv2-step2-counter-btn"
                  onClick={() => changeChildCount(1)}
                  disabled={childCount >= MAX_PEOPLE}
                  aria-label="Increase children"
                >
                  +
                </button>
              </div>
            </div>
          </div>

          <h3 className="fv2-step2-section-h">
            What are the ages of everyone in your household?
          </h3>

          {adultCount > 0 && (
            <div className="fv2-step2-ages-group">
              <span className="fv2-step2-ages-label">Adults (18+)</span>
              {adultAges.map((age, idx) => {
                const err = adultErrors[idx];
                const inputId = `fv2-adult-age-${idx}`;
                return (
                  <div className="fv2-step2-age-row" key={`adult-${idx}`}>
                    <span className="fv2-step2-age-icon">
                      <UserIcon size={20} />
                    </span>
                    <label htmlFor={inputId} className="fv2-step2-age-label">
                      Adult {idx + 1}
                    </label>
                    <div className="fv2-step2-age-input-wrap">
                      <input
                        id={inputId}
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        maxLength={3}
                        value={age}
                        onChange={(e) => setAdultAge(idx, e.target.value)}
                        className={
                          err
                            ? "fv2-step2-age-input fv2-step2-age-input-error"
                            : "fv2-step2-age-input"
                        }
                        aria-invalid={err ? "true" : "false"}
                      />
                      <span className="fv2-step2-age-suffix">years old</span>
                    </div>
                    {err && (
                      <p className="fv2-step2-age-error" role="alert">
                        {err}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {childCount > 0 && (
            <div className="fv2-step2-ages-group">
              <span className="fv2-step2-ages-label">Children (Under 18)</span>
              {childAges.map((age, idx) => {
                const err = childErrors[idx];
                const inputId = `fv2-child-age-${idx}`;
                return (
                  <div className="fv2-step2-age-row" key={`child-${idx}`}>
                    <span className="fv2-step2-age-icon">
                      <UserIcon size={20} />
                    </span>
                    <label htmlFor={inputId} className="fv2-step2-age-label">
                      Child {idx + 1}
                    </label>
                    <div className="fv2-step2-age-input-wrap">
                      <input
                        id={inputId}
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        maxLength={3}
                        value={age}
                        onChange={(e) => setChildAge(idx, e.target.value)}
                        className={
                          err
                            ? "fv2-step2-age-input fv2-step2-age-input-error"
                            : "fv2-step2-age-input"
                        }
                        aria-invalid={err ? "true" : "false"}
                      />
                      <span className="fv2-step2-age-suffix">years old</span>
                    </div>
                    {err && (
                      <p className="fv2-step2-age-error" role="alert">
                        {err}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <button type="submit" className="fv2-step2-submit" disabled={submitting}>
            {submitting ? "Saving..." : "Save & Continue"}
            <ArrowRightIcon />
          </button>

          {apiError && (
            <p className="fv2-step2-age-error" role="alert">{apiError}</p>
          )}

          <p className="fv2-step2-secure">
            <LockIcon /> Your information is secure and confidential.
          </p>
        </form>
      </div>
    </FunnelLayout>
  );
}
